import { useState, useEffect, useRef, useMemo } from "react";
import { perfumeAPI, ordersAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Delete,
  Check,
  MessageCircle,
  Globe,
  Music,
  X,
  ShoppingCart,
  Clock,
  Trash2,
  Plus,
  Printer,
  User,
  MapPin,
} from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const FORMATS = ["30ml", "50ml", "100ml", "200ml", "500ml"];
const CHANNELS = [
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "#10B981" },
  { id: "website", label: "Sito", icon: Globe, color: "#3B82F6" },
  { id: "tiktok_shop", label: "TikTok", icon: Music, color: "#EC4899" },
];

// Fixed prices
const FIXED_PRICES = {
  "30ml": { whatsapp: 6, website: 6, tiktok_shop: 7 },
  "50ml": { whatsapp: 10, website: 10, tiktok_shop: 11 },
  "100ml": { whatsapp: 15, website: 15, tiktok_shop: 16 },
  "200ml": { whatsapp: 25, website: 25, tiktok_shop: 26 },
  "500ml": { whatsapp: 60, website: 60, tiktok_shop: 60 },
};

// Prezzi speciali per profumi specifici (Sultan ST10, C0K, M35)
// Formato: { "CODICE": { "formato": { "canale": prezzo } } }
const SPECIAL_PRICES = {
  "ST10": {
    "30ml": { whatsapp: 8, website: 8, tiktok_shop: 9 },
    "50ml": { whatsapp: 12, website: 12, tiktok_shop: 13 },
    "100ml": { whatsapp: 20, website: 20, tiktok_shop: 20 },
    "200ml": { whatsapp: 30, website: 30, tiktok_shop: 30 },
    "500ml": { whatsapp: 70, website: 70, tiktok_shop: 70 },
  },
  "C0K": {
    "30ml": { whatsapp: 8, website: 8, tiktok_shop: 9 },
    "50ml": { whatsapp: 12, website: 12, tiktok_shop: 13 },
    "100ml": { whatsapp: 20, website: 20, tiktok_shop: 20 },
    "200ml": { whatsapp: 30, website: 30, tiktok_shop: 30 },
    "500ml": { whatsapp: 70, website: 70, tiktok_shop: 70 },
  },
  "M35": {
    "30ml": { whatsapp: 8, website: 8, tiktok_shop: 9 },
    "50ml": { whatsapp: 12, website: 12, tiktok_shop: 13 },
    "100ml": { whatsapp: 20, website: 20, tiktok_shop: 20 },
    "200ml": { whatsapp: 30, website: 30, tiktok_shop: 30 },
    "500ml": { whatsapp: 70, website: 70, tiktok_shop: 70 },
  }
};

// Funzione per ottenere il prezzo considerando i prezzi speciali
const getPrice = (perfumeCode, format, channel) => {
  // Controlla se il profumo ha un prezzo speciale
  if (perfumeCode && SPECIAL_PRICES[perfumeCode.toUpperCase()]) {
    const special = SPECIAL_PRICES[perfumeCode.toUpperCase()];
    if (special[format]) {
      // Se il prezzo speciale ha i canali, usa quello del canale
      if (typeof special[format] === 'object') {
        return special[format][channel] || 0;
      }
      // Retrocompatibilità: numero semplice
      return special[format];
    }
  }
  // Altrimenti usa il prezzo standard
  return FIXED_PRICES[format]?.[channel] || 0;
};

// Natural sorting for perfume codes
const naturalSort = (a, b) => {
  const extractNum = (str) => {
    const match = str.match(/^(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  };
  const numA = extractNum(a.code);
  const numB = extractNum(b.code);
  if (numA !== numB) return numA - numB;
  return a.code.localeCompare(b.code);
};

const QuickOrder = () => {
  const [perfumes, setPerfumes] = useState([]);
  const [todayOrders, setTodayOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchCode, setSearchCode] = useState("");
  const [selectedFormat, setSelectedFormat] = useState("100ml");
  const [selectedChannel, setSelectedChannel] = useState("whatsapp");
  const [cart, setCart] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  // Customer info for WhatsApp orders
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [selectedCourier, setSelectedCourier] = useState("GLS"); // GLS or NAPOLI
  const [paymentMethod, setPaymentMethod] = useState("paid"); // paid or cod (contrassegno)

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDate = today.toISOString();

      const [perfumesRes, ordersRes] = await Promise.all([
        perfumeAPI.getAll(),
        ordersAPI.getAll({ start_date: startDate, limit: 100 })
      ]);
      setPerfumes(perfumesRes.data);
      // Handle new paginated response format
      const ordersData = ordersRes.data;
      setTodayOrders(ordersData.orders || ordersData || []);
    } catch (error) {
      toast.error("Errore caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  // Find matching perfumes based on search code - exact match first, then partial matches (memoized)
  const matchingPerfumes = useMemo(() => {
    if (!searchCode) return [];
    return perfumes
      .filter(p => p.code.toLowerCase().startsWith(searchCode.toLowerCase()) ||
                   p.code.toLowerCase().includes(searchCode.toLowerCase()))
      .sort((a, b) => {
        const searchLower = searchCode.toLowerCase();
        const aCode = a.code.toLowerCase();
        const bCode = b.code.toLowerCase();
        
        // Exact match has highest priority
        const aExact = aCode === searchLower;
        const bExact = bCode === searchLower;
        if (aExact && !bExact) return -1;
        if (bExact && !aExact) return 1;
        
        // Then startsWith has priority
        const aStarts = aCode.startsWith(searchLower);
        const bStarts = bCode.startsWith(searchLower);
        if (aStarts && !bStarts) return -1;
        if (bStarts && !aStarts) return 1;
        
        // Finally sort numerically
        const numA = parseInt(a.code.replace(/\D/g, '')) || 0;
        const numB = parseInt(b.code.replace(/\D/g, '')) || 0;
        if (numA !== numB) return numA - numB;
        return a.code.localeCompare(b.code);
      })
      .slice(0, 6);
  }, [searchCode, perfumes]);

  const handleNumpadClick = (value) => {
    if (value === "DEL") {
      setSearchCode(prev => prev.slice(0, -1));
    } else {
      // All values including "C" are added to search code
      setSearchCode(prev => prev + value);
    }
  };

  const handleAddToCart = (perfume) => {
    const price = getPrice(perfume.code, selectedFormat, selectedChannel);
    // Check if same perfume+format already in cart, if so increase quantity
    const existingIndex = cart.findIndex(
      item => item.perfume_id === perfume.id && item.format === selectedFormat
    );
    
    if (existingIndex >= 0) {
      setCart(prev => prev.map((item, i) => 
        i === existingIndex ? { ...item, quantity: item.quantity + 1 } : item
      ));
      toast.success(`${perfume.code} - quantità aumentata`);
    } else {
      setCart(prev => [...prev, {
        perfume_id: perfume.id,
        perfume_code: perfume.code,
        perfume_name: perfume.name,
        format: selectedFormat,
        price: price,
        quantity: 1
      }]);
      toast.success(`${perfume.code} aggiunto - ${selectedFormat}`);
    }
    setSearchCode("");
  };

  const handleRemoveFromCart = (index) => {
    setCart(prev => prev.filter((_, i) => i !== index));
  };

  const handleClearCart = () => {
    setCart([]);
    setSearchCode("");
  };

  const getCartTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
  };

  const handleUpdateCartItemPrice = (index, newPrice) => {
    setCart(prev => prev.map((item, i) => 
      i === index ? { ...item, price: newPrice } : item
    ));
  };

  const handleUpdateCartItemQuantity = (index, newQty) => {
    if (newQty < 1) {
      handleRemoveFromCart(index);
      return;
    }
    setCart(prev => prev.map((item, i) => 
      i === index ? { ...item, quantity: newQty } : item
    ));
  };

  const handleConfirmOrder = () => {
    if (cart.length === 0) {
      toast.error("Aggiungi almeno un profumo");
      return;
    }
    
    // For WhatsApp, ask for customer info
    if (selectedChannel === "whatsapp") {
      setShowCustomerDialog(true);
    } else {
      submitOrder();
    }
  };

  const submitOrder = async (name = null, address = null) => {
    setSubmitting(true);
    try {
      // Expand cart items based on quantity
      const expandedItems = [];
      cart.forEach(item => {
        const qty = item.quantity || 1;
        for (let i = 0; i < qty; i++) {
          expandedItems.push({
            perfume_id: item.perfume_id,
            format: item.format,
            quantity: qty  // Include quantity info for display
          });
        }
      });
      
      const orderData = {
        items: cart.map(item => ({
          perfume_id: item.perfume_id,
          format: item.format,
          quantity: item.quantity || 1
        })),
        channel: selectedChannel,
        customer_name: name || customerName || null,
        customer_address: address || customerAddress || null,
        courier: selectedChannel === "whatsapp" ? selectedCourier : "GLS",
        payment_method: paymentMethod,
      };

      await ordersAPI.create(orderData);
      
      const totalItems = cart.reduce((sum, item) => sum + (item.quantity || 1), 0);
      toast.success(`Ordine confermato! ${totalItems} articoli - €${getCartTotal()}`);
      
      setCart([]);
      setCustomerName("");
      setCustomerAddress("");
      setSelectedCourier("GLS");
      setPaymentMethod("paid");
      setShowCustomerDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore creazione ordine");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrintOrder = (order) => {
    const printWindow = window.open('', '_blank');
    
    // Group items by format
    const itemsByFormat = order.items.reduce((acc, item) => {
      if (!acc[item.format]) acc[item.format] = [];
      acc[item.format].push(item);
      return acc;
    }, {});

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Ordine ${order.id.slice(0, 8)}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 400px; margin: 0 auto; }
          .logo { text-align: center; font-size: 28px; font-weight: bold; margin-bottom: 10px; letter-spacing: 2px; }
          .logo-sub { text-align: center; font-size: 12px; letter-spacing: 4px; color: #666; margin-bottom: 20px; }
          .customer { background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .customer-name { font-size: 24px; font-weight: bold; margin-bottom: 5px; }
          .customer-address { font-size: 18px; color: #333; line-height: 1.4; }
          .section { margin-bottom: 15px; }
          .section-title { font-size: 14px; font-weight: bold; color: #666; margin-bottom: 8px; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
          .item { display: flex; justify-content: space-between; padding: 4px 0; font-size: 14px; }
          .item-code { font-weight: bold; }
          .total { font-size: 20px; font-weight: bold; text-align: right; margin-top: 15px; padding-top: 10px; border-top: 2px solid #000; }
          .footer { text-align: center; font-size: 11px; color: #999; margin-top: 30px; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <div class="logo">EQUIVALENCIA</div>
        <div class="logo-sub">EQUIPE</div>
        
        ${order.customer_name || order.customer_address ? `
        <div class="customer">
          ${order.customer_name ? `<div class="customer-name">${order.customer_name}</div>` : ''}
          ${order.customer_address ? `<div class="customer-address">${order.customer_address}</div>` : ''}
        </div>
        ` : ''}
        
        ${Object.entries(itemsByFormat).map(([fmt, items]) => `
          <div class="section">
            <div class="section-title">${fmt} (${items.length})</div>
            ${items.map(item => `
              <div class="item">
                <span class="item-code">${item.perfume_code}</span>
                <span>${item.perfume_name}</span>
              </div>
            `).join('')}
          </div>
        `).join('')}
        
        <div class="total">TOTALE: €${order.total_amount.toFixed(2)}</div>
        
        <div class="footer">
          Ordine del ${format(new Date(order.created_at), "dd/MM/yyyy HH:mm", { locale: it })}
        </div>
        
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Per il prezzo corrente mostrato in alto, uso un prezzo di default (senza codice specifico)
  const currentPrice = FIXED_PRICES[selectedFormat]?.[selectedChannel] || 0;

  // Funzione per mostrare se un profumo ha prezzo speciale
  const hasSpecialPrice = (code) => {
    return code && SPECIAL_PRICES[code.toUpperCase()];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#C9A227] animate-pulse">Caricamento...</div>
      </div>
    );
  }

  return (
    <div data-testid="quick-order-page" className="animate-fadeIn">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-2xl md:text-3xl font-serif text-gray-900 mb-1">Registra Ordine</h1>
        <p className="text-gray-500 text-sm">Aggiungi profumi al carrello e conferma</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column - Format, Channel, Price */}
        <div className="space-y-3">
          {/* Format Selection with Price */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 text-sm">FORMATO</h3>
              <span className="text-xl font-bold text-[#C9A227]">€{currentPrice}</span>
            </div>
            <div className="grid grid-cols-5 gap-1.5">
              {FORMATS.map((fmt) => {
                const price = FIXED_PRICES[fmt]?.[selectedChannel] || 0;
                return (
                  <button
                    key={fmt}
                    data-testid={`format-${fmt}`}
                    onClick={() => setSelectedFormat(fmt)}
                    className={`py-2 rounded-lg text-center transition-all ${
                      selectedFormat === fmt
                        ? "bg-[#C9A227] text-white shadow-md scale-105"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    <div className="font-bold text-sm">{fmt.replace("ml", "")}</div>
                    <div className="text-[10px] opacity-75">€{price}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Channel Selection */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-3 text-sm">CANALE</h3>
            <div className="grid grid-cols-3 gap-2">
              {CHANNELS.map((channel) => {
                const Icon = channel.icon;
                const isSelected = selectedChannel === channel.id;
                return (
                  <button
                    key={channel.id}
                    data-testid={`channel-${channel.id}`}
                    onClick={() => setSelectedChannel(channel.id)}
                    className={`py-3 px-2 rounded-lg font-medium transition-all flex flex-col items-center gap-1.5 ${
                      isSelected ? "shadow-md scale-105" : "bg-gray-100 hover:bg-gray-200"
                    }`}
                    style={{
                      backgroundColor: isSelected ? channel.color : undefined,
                      color: isSelected ? "white" : "#374151"
                    }}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-xs">{channel.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Search Results */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <h3 className="font-semibold text-gray-900 text-sm mb-2">RISULTATI RICERCA</h3>
            {searchCode ? (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {matchingPerfumes.map((p) => {
                  const specialPrice = hasSpecialPrice(p.code);
                  const priceForFormat = getPrice(p.code, selectedFormat, selectedChannel);
                  return (
                    <button
                      key={p.id}
                      onClick={() => handleAddToCart(p)}
                      className={`w-full p-2.5 text-left rounded-lg border transition-colors flex items-center justify-between group ${
                        specialPrice 
                          ? "bg-amber-50 hover:bg-amber-100 border-amber-200 hover:border-amber-400"
                          : "bg-gray-50 hover:bg-green-50 border-gray-100 hover:border-green-300"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[#C9A227] font-bold">{p.code}</span>
                        <span className="text-gray-700 text-sm">{p.name}</span>
                        {specialPrice && (
                          <Badge className="bg-amber-500 text-white text-[9px] px-1.5">SPECIAL</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`font-bold ${specialPrice ? "text-amber-600" : "text-gray-500"}`}>
                          €{priceForFormat}
                        </span>
                        <Plus className="w-5 h-5 text-green-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </button>
                  );
                })}
                {matchingPerfumes.length === 0 && (
                  <p className="text-gray-400 text-center py-2 text-sm">Nessun risultato</p>
                )}
              </div>
            ) : (
              <p className="text-gray-400 text-sm text-center py-4">Digita il codice...</p>
            )}
          </div>
        </div>

        {/* Middle Column - Numpad + Cart */}
        <div className="space-y-3">
          {/* Numpad */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="mb-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-2">
                Codice Profumo
              </p>
              <input
                type="text"
                value={searchCode}
                onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
                placeholder="Digita codice (es: 10, M21, 536)"
                className="w-full bg-gray-100 rounded-xl p-3 text-2xl font-bold tabular-nums text-gray-900 text-center focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                data-testid="search-code-input"
              />
            </div>

            <div className="grid grid-cols-4 gap-2">
              {["7", "8", "9", "M", "4", "5", "6", "V", "1", "2", "3", "S", "C", "0", ".", "DEL"].map((key) => (
                <button
                  key={key}
                  data-testid={`numpad-${key}`}
                  onClick={() => handleNumpadClick(key)}
                  className={`h-11 rounded-xl text-lg font-bold transition-all active:scale-95 ${
                    key === "DEL"
                      ? "bg-red-100 text-red-600 hover:bg-red-200"
                      : ["M", "V", "S", "C"].includes(key)
                      ? "bg-[#C9A227]/20 text-[#C9A227] hover:bg-[#C9A227]/30"
                      : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                  }`}
                >
                  {key === "DEL" ? <Delete className="w-5 h-5 mx-auto" /> : key}
                </button>
              ))}
            </div>
          </div>

          {/* Cart */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-[#C9A227]" />
                CARRELLO
              </h3>
              <Badge className="bg-[#C9A227]/10 text-[#C9A227] border-[#C9A227]/30">
                {cart.reduce((sum, item) => sum + (item.quantity || 1), 0)} articoli
              </Badge>
            </div>

            <div className="space-y-1.5 max-h-40 overflow-y-auto mb-3">
              {cart.length === 0 ? (
                <p className="text-gray-400 text-center py-4 text-sm">Carrello vuoto</p>
              ) : (
                cart.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded-lg group"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[#C9A227] font-bold text-sm">{item.perfume_code}</span>
                      <Badge className="text-[10px] px-1.5 py-0 bg-gray-200 text-gray-600">
                        {item.format}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1">
                      {/* Quantity controls */}
                      <div className="flex items-center gap-0.5 mr-2">
                        <button
                          onClick={() => handleUpdateCartItemQuantity(index, (item.quantity || 1) - 1)}
                          className="w-5 h-5 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded text-xs font-bold"
                        >
                          -
                        </button>
                        <span className="w-6 text-center font-bold text-sm">{item.quantity || 1}</span>
                        <button
                          onClick={() => handleUpdateCartItemQuantity(index, (item.quantity || 1) + 1)}
                          className="w-5 h-5 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded text-xs font-bold"
                        >
                          +
                        </button>
                      </div>
                      <span className="text-gray-400 text-xs">€</span>
                      <input
                        type="number"
                        step="0.01"
                        value={item.price}
                        onChange={(e) => handleUpdateCartItemPrice(index, parseFloat(e.target.value) || 0)}
                        className="w-12 text-right font-bold text-sm bg-transparent border-b border-transparent hover:border-gray-300 focus:border-[#C9A227] focus:outline-none"
                        data-testid={`cart-price-${index}`}
                      />
                      <button
                        onClick={() => handleRemoveFromCart(index)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-600 transition-all"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Total */}
            <div className="flex justify-between items-center py-2 border-t border-gray-200">
              <span className="text-gray-600 font-medium">TOTALE</span>
              <span className="text-2xl font-bold text-[#C9A227]">€{getCartTotal()}</span>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Button
                variant="outline"
                onClick={handleClearCart}
                disabled={cart.length === 0}
                className="h-11 text-gray-600 border-gray-300 hover:bg-gray-100 rounded-xl font-semibold"
              >
                <X className="w-4 h-4 mr-1" />
                Svuota
              </Button>
              <Button
                onClick={handleConfirmOrder}
                disabled={cart.length === 0 || submitting}
                data-testid="submit-order"
                className="h-11 bg-[#C9A227] text-white hover:bg-[#D4AF37] rounded-xl font-semibold shadow-lg disabled:opacity-50"
              >
                <Check className="w-4 h-4 mr-1" />
                {submitting ? "..." : "Conferma"}
              </Button>
            </div>
          </div>
        </div>

        {/* Right Column - Today's Orders */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#C9A227]" />
              ORDINI OGGI
            </h3>
            <Badge className="bg-[#C9A227]/10 text-[#C9A227] border-[#C9A227]/30">
              {todayOrders.length}
            </Badge>
          </div>

          <div className="space-y-2 max-h-[450px] overflow-y-auto">
            {todayOrders.length === 0 ? (
              <p className="text-gray-400 text-center py-8 text-sm">Nessun ordine oggi</p>
            ) : (
              todayOrders.map((order) => (
                <div
                  key={order.id}
                  className="p-3 bg-gray-50 rounded-lg border border-gray-100 group"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge 
                        className="text-[10px] px-1.5 py-0"
                        style={{ 
                          backgroundColor: `${CHANNELS.find(c => c.id === order.channel)?.color || '#666'}20`,
                          color: CHANNELS.find(c => c.id === order.channel)?.color || '#666',
                        }}
                      >
                        {order.channel}
                      </Badge>
                      <span className="text-xs text-gray-400">
                        {format(new Date(order.created_at), "HH:mm")}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="font-bold text-[#C9A227]">€{order.total_amount}</span>
                      <button
                        onClick={() => handlePrintOrder(order)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-blue-500 transition-all"
                        title="Stampa"
                      >
                        <Printer className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {order.customer_name && (
                    <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                      <User className="w-3 h-3" />
                      {order.customer_name}
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-1">
                    {order.items.slice(0, 4).map((item, i) => (
                      <span key={i} className="text-xs bg-white px-1.5 py-0.5 rounded border">
                        {item.perfume_code}
                      </span>
                    ))}
                    {order.items.length > 4 && (
                      <span className="text-xs text-gray-400">+{order.items.length - 4}</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Today's Total */}
          {todayOrders.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="text-gray-500 text-sm">Totale oggi</span>
                <span className="font-bold text-lg text-[#C9A227]">
                  €{todayOrders.reduce((sum, o) => sum + o.total_amount, 0).toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Customer Info Dialog for WhatsApp */}
      <Dialog open={showCustomerDialog} onOpenChange={setShowCustomerDialog}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif text-gray-900 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-500" />
              Dati Cliente WhatsApp
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                <User className="w-4 h-4" />
                Nome Cliente
              </label>
              <Input
                data-testid="customer-name-input"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Mario Rossi"
                className="bg-gray-50 border-gray-200"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4" />
                Indirizzo di Spedizione
              </label>
              <Textarea
                data-testid="customer-address-input"
                value={customerAddress}
                onChange={(e) => setCustomerAddress(e.target.value)}
                placeholder="Via Roma 123, 00100 Roma"
                className="bg-gray-50 border-gray-200 min-h-[80px]"
              />
            </div>
            
            {/* Courier Selection */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Corriere
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedCourier("GLS")}
                  data-testid="courier-gls"
                  className={`p-3 rounded-lg border-2 transition-all ${
                    selectedCourier === "GLS" 
                      ? "border-yellow-500 bg-yellow-50 text-yellow-700" 
                      : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <span className="font-bold">GLS</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedCourier("NAPOLI")}
                  data-testid="courier-napoli"
                  className={`p-3 rounded-lg border-2 transition-all ${
                    selectedCourier === "NAPOLI" 
                      ? "border-purple-500 bg-purple-50 text-purple-700" 
                      : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <span className="font-bold">Napoli</span>
                </button>
              </div>
            </div>
            
            {/* Payment Method */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Metodo di Pagamento
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setPaymentMethod("paid")}
                  data-testid="payment-paid"
                  className={`p-3 rounded-lg border-2 transition-all ${
                    paymentMethod === "paid" 
                      ? "border-green-500 bg-green-50 text-green-700" 
                      : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <span className="font-bold">✓ Pagato</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMethod("cod")}
                  data-testid="payment-cod"
                  className={`p-3 rounded-lg border-2 transition-all ${
                    paymentMethod === "cod" 
                      ? "border-orange-500 bg-orange-50 text-orange-700" 
                      : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <span className="font-bold">Contrassegno</span>
                </button>
              </div>
            </div>
            
            <div className="pt-2">
              <p className="text-sm text-gray-500 mb-3">
                Totale: <span className="font-bold text-[#C9A227]">€{getCartTotal()}</span> ({cart.length} articoli)
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCustomerDialog(false);
                    submitOrder();
                  }}
                  className="border-gray-300"
                >
                  Salta
                </Button>
                <Button
                  onClick={() => submitOrder(customerName, customerAddress)}
                  disabled={submitting}
                  className="bg-green-500 hover:bg-green-600 text-white"
                >
                  {submitting ? "..." : "Conferma"}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuickOrder;
